import { useState, useRef, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { promozioniAPI, tenantsAPI } from '../utils/api';
import { usePermissions } from '../hooks/usePermissions';
import { PromozioneAllegatoResponse } from '../types/promozioneAllegato';
import AllegatoPreviewModal from './AllegatoPreviewModal';
import toast from 'react-hot-toast';
import {
  Loader2,
  Upload,
  Trash2,
  Download,
  Eye,
  Paperclip,
  FileText,
  X,
  Image as ImageIcon,
  FileSpreadsheet,
} from 'lucide-react';

interface PromozioneAllegatiManagerProps {
  promozioneId: string;
}

const MAX_ATTACHMENTS = 20;
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
const DEFAULT_ALLOWED_FORMATS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg'];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function getExtension(filename: string): string {
  const idx = filename.lastIndexOf('.');
  if (idx === -1 || idx === filename.length - 1) return '';
  return filename.slice(idx + 1).toLowerCase();
}

function iconForExtension(ext: string) {
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return ImageIcon;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return FileSpreadsheet;
  return FileText;
}

export default function PromozioneAllegatiManager({
  promozioneId,
}: PromozioneAllegatiManagerProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { hasPermission } = usePermissions();

  const canUpload = hasPermission('promozioni:attachments:upload');
  const canDownload = hasPermission('promozioni:attachments:download');
  const canDelete = hasPermission('promozioni:attachments:delete');

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingDescrizione, setPendingDescrizione] = useState('');
  const [dragOverZone, setDragOverZone] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [previewAllegato, setPreviewAllegato] = useState<PromozioneAllegatoResponse | null>(null);
  const closePreview = useCallback(() => setPreviewAllegato(null), []);

  // Tenant whitelist
  const { data: tenantMe } = useQuery({
    queryKey: ['tenant', 'me'],
    queryFn: async () => (await tenantsAPI.getMe()).data,
    staleTime: 5 * 60 * 1000,
  });

  const allowedFormats = useMemo(
    () =>
      tenantMe?.allowed_promo_attachment_formats && tenantMe.allowed_promo_attachment_formats.length > 0
        ? tenantMe.allowed_promo_attachment_formats
        : DEFAULT_ALLOWED_FORMATS,
    [tenantMe]
  );

  const acceptAttribute = useMemo(
    () => allowedFormats.map((ext) => `.${ext}`).join(','),
    [allowedFormats]
  );

  const { data: allegati = [], isLoading } = useQuery({
    queryKey: ['promozione-allegati', promozioneId],
    queryFn: async () => (await promozioniAPI.getAllegati(promozioneId)).data,
    enabled: canDownload,
  });

  const uploadMutation = useMutation({
    mutationFn: async ({ file, descrizione }: { file: File; descrizione?: string }) => {
      const response = await promozioniAPI.uploadAllegato(promozioneId, file, descrizione);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promozione-allegati', promozioneId] });
      queryClient.invalidateQueries({ queryKey: ['promozioni'] });
      queryClient.invalidateQueries({ queryKey: ['promozione', promozioneId] });
      toast.success('Allegato caricato');
      setPendingFile(null);
      setPendingDescrizione('');
    },
    onError: (error: any) => {
      const detail = error?.response?.data?.detail;
      const status = error?.response?.status;
      const messageMap: Record<string, string> = {
        max_attachments_exceeded: `Limite raggiunto (max ${MAX_ATTACHMENTS} allegati per promozione).`,
        file_too_large: `File troppo grande (max ${MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB).`,
        unsupported_media_type: 'Formato non ammesso dalla whitelist tenant.',
        promozione_not_found: 'Promozione non trovata.',
        forbidden: 'Permesso negato.',
      };
      const fallback =
        status === 413
          ? messageMap.file_too_large
          : status === 415
          ? messageMap.unsupported_media_type
          : status === 409
          ? messageMap.max_attachments_exceeded
          : 'Errore nel caricamento';
      toast.error((typeof detail === 'string' && messageMap[detail]) || (typeof detail === 'string' ? detail : fallback));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (allegatoId: string) => promozioniAPI.deleteAllegato(promozioneId, allegatoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promozione-allegati', promozioneId] });
      queryClient.invalidateQueries({ queryKey: ['promozioni'] });
      queryClient.invalidateQueries({ queryKey: ['promozione', promozioneId] });
      toast.success('Allegato eliminato');
      setDeleteConfirmId(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || "Errore nell'eliminazione");
      setDeleteConfirmId(null);
    },
  });

  const validateFile = useCallback(
    (file: File): string | null => {
      const ext = getExtension(file.name);
      if (!ext) {
        return `"${file.name}" non ha un'estensione riconosciuta.`;
      }
      if (!allowedFormats.includes(ext)) {
        return `"${file.name}" (.${ext}) non è ammesso. Formati consentiti: ${allowedFormats.join(', ')}.`;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        return `"${file.name}" supera i 25 MB (${formatFileSize(file.size)}).`;
      }
      if (allegati.length >= MAX_ATTACHMENTS) {
        return `Limite raggiunto: max ${MAX_ATTACHMENTS} allegati per promozione.`;
      }
      return null;
    },
    [allowedFormats, allegati.length]
  );

  const handleFileSelected = useCallback(
    (file: File) => {
      const error = validateFile(file);
      if (error) {
        toast.error(error);
        return;
      }
      setPendingFile(file);
    },
    [validateFile]
  );

  const handleConfirmUpload = () => {
    if (!pendingFile) return;
    uploadMutation.mutate({
      file: pendingFile,
      descrizione: pendingDescrizione.trim() || undefined,
    });
  };

  const handleDragOverZone = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverZone(true);
  };

  const handleDragLeaveZone = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverZone(false);
  };

  const handleDropZone = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverZone(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelected(files[0]);
    }
  };

  if (!canDownload) {
    return (
      <div className="text-sm text-gray-500 italic">
        Non hai i permessi per visualizzare gli allegati di questa promozione.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Paperclip className="h-5 w-5 text-gray-500" />
          <h3 className="text-base font-semibold text-gray-900">Allegati</h3>
          <span className="text-sm text-gray-500">
            {allegati.length}/{MAX_ATTACHMENTS}
          </span>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />
          Caricamento allegati...
        </div>
      ) : allegati.length === 0 ? (
        <div className="text-center py-6 text-gray-500 border border-dashed border-gray-200 rounded-lg">
          <Paperclip className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">Nessun allegato per questa promozione</p>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left font-medium">File</th>
                <th className="px-3 py-2 text-left font-medium">Descrizione</th>
                <th className="px-3 py-2 text-left font-medium">Dimensione</th>
                <th className="px-3 py-2 text-left font-medium">Data</th>
                <th className="px-3 py-2 text-left font-medium">Autore</th>
                <th className="px-3 py-2 text-right font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {allegati.map((allegato: PromozioneAllegatoResponse) => {
                const ext = getExtension(allegato.filename);
                const Icon = iconForExtension(ext);
                return (
                  <tr key={allegato.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Icon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="truncate text-gray-900" title={allegato.filename}>
                          {allegato.filename}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {allegato.descrizione || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                      {formatFileSize(allegato.file_size_bytes)}
                    </td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                      {new Date(allegato.created_at).toLocaleDateString('it-IT')}
                    </td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                      {allegato.created_by_name || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setPreviewAllegato(allegato)}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                          title="Visualizza allegato"
                        >
                          <Eye className="h-3 w-3" />
                          Visualizza
                        </button>
                        <a
                          href={allegato.presigned_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={allegato.filename}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                          title="Scarica allegato"
                        >
                          <Download className="h-3 w-3" />
                          Scarica
                        </a>
                        {canDelete && (
                          <button
                            type="button"
                            onClick={() => setDeleteConfirmId(allegato.id)}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100"
                            title="Elimina allegato"
                          >
                            <Trash2 className="h-3 w-3" />
                            Elimina
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {canUpload && allegati.length < MAX_ATTACHMENTS && (
        <div className="space-y-3">
          <div
            onDragOver={handleDragOverZone}
            onDragLeave={handleDragLeaveZone}
            onDrop={handleDropZone}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              dragOverZone
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
            }`}
          >
            <Upload
              className={`h-7 w-7 mx-auto mb-2 ${
                dragOverZone ? 'text-blue-500' : 'text-gray-400'
              }`}
            />
            <p className="text-sm text-gray-600">
              Trascina un file qui oppure{' '}
              <span className="text-blue-600 font-medium">sfoglia</span>
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Formati ammessi: {allowedFormats.join(', ').toUpperCase()} - Max 25 MB
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptAttribute}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileSelected(file);
                e.target.value = '';
              }}
              className="hidden"
            />
          </div>

          {pendingFile && (
            <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900 truncate" title={pendingFile.name}>
                      {pendingFile.name}
                    </p>
                    <p className="text-xs text-gray-500">{formatFileSize(pendingFile.size)}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setPendingFile(null);
                    setPendingDescrizione('');
                  }}
                  disabled={uploadMutation.isPending}
                  className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-3 space-y-2">
                <label className="block text-xs font-medium text-gray-700">
                  Descrizione (opzionale)
                </label>
                <input
                  type="text"
                  value={pendingDescrizione}
                  onChange={(e) => setPendingDescrizione(e.target.value)}
                  placeholder="es. Listino 2026"
                  maxLength={200}
                  className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  disabled={uploadMutation.isPending}
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setPendingFile(null);
                      setPendingDescrizione('');
                    }}
                    disabled={uploadMutation.isPending}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  >
                    Annulla
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmUpload}
                    disabled={uploadMutation.isPending}
                    className="inline-flex items-center gap-1 px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {uploadMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    Carica
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {canUpload && allegati.length >= MAX_ATTACHMENTS && (
        <div className="text-xs text-gray-500 italic border border-dashed border-gray-200 rounded-lg p-3 text-center">
          Hai raggiunto il limite di {MAX_ATTACHMENTS} allegati. Elimina un allegato per caricarne uno nuovo.
        </div>
      )}

      {previewAllegato && (
        <AllegatoPreviewModal
          presignedUrl={previewAllegato.presigned_url}
          filename={previewAllegato.filename}
          contentType={previewAllegato.content_type}
          onClose={closePreview}
        />
      )}

      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-3">Conferma eliminazione</h3>
            <p className="text-sm text-gray-600 mb-6">
              Sei sicuro di voler eliminare questo allegato? L'operazione non puo essere annullata
              ma il file resta archiviato per audit.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(deleteConfirmId)}
                disabled={deleteMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
